import React, { useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { NewPitVM } from './NewPitVM';
import { FlatButton } from 'nrcs-design-system';
import { mdiTargetVariant, mdiCursorMove } from '@mdi/js';
import { Input } from 'reactstrap';
import useImageLoaded from 'cd-eft/utils/useImageLoaded';
import { toDecimalPrecision } from 'cd-eft/utils/mathUtilities';
import { Notification } from '../../common/Notification/Notification';
import rectangularStructureDiagram from '../../../assets/images/rectangle_layout.jpg';
import { UserLeavingWarning } from 'cd-eft/components/common/UserLeavingWarning/UserLeavingWarning';
import roundStructureDiagram from '../../../assets/images/round_layout.jpg';
import dikedStructureDiagram from '../../../assets/images/diked_layout.jpg';
import stores from '../../../models/Stores';
import './newPit.scss';

const { pitDesignStore } = stores.AppStore;

const NewPitComponent = (props) => {
  const history = useHistory();
  const vmRef = useRef<NewPitVM>();
  if (!vmRef.current) {
    vmRef.current = new NewPitVM(props);
  }
  const vm = vmRef.current;
  const [ref, loaded, onLoad] = useImageLoaded();

  useEffect(() => {
    vm.getErrors();
    vm.onMount(history);
    function unmount() {
      // breakdown/cleanup code
      vm.onUnmount();
    }
    return unmount;
  }, []);

  return (
    <div className="structure-new-pit-form">
      {/* popups  */}
      {(stores.AppStore.designStore.hasProject()) && (pitDesignStore.pendingEdits.design || pitDesignStore.pendingEdits.unsavedEdits || pitDesignStore.pendingEdits.name) && (
        <UserLeavingWarning
          when={stores.AppStore.designStore.hasProject() && (pitDesignStore.pendingEdits.design || pitDesignStore.pendingEdits.unsavedEdits || pitDesignStore.pendingEdits.name)}
          section="Pit Design"
          onOK={vm.userLeavingWarningOnOk}
          okText="Return"
          text={
            vm.notification.openPit
          }
        />
      )}

      <div className="pit-name">
        <Input
          placeholder="Pit Name...."
          className={'mr-2' + vm.errorClass('name')}
          id="name"
          aria-required="true"
          aria-label="Pit Name"
          name="name"
          value={vm.newPitForm.name}
          onChange={(e) => vm.handleInputChange(e)}
        />

        <Input
          name="type"
          type="select"
          aria-required="true"
          aria-label="Pit Type"
          id="pit-type"
          value={vm.newPitForm.type}
          onChange={(e) => vm.handleInputChange(e)}
          hidden={pitDesignStore.editDesign}
        >
          <option value="">- Select Pit Type -</option>
          <option value="Rectangular Pit">Rectangular Pit</option>
          <option value="Round Pit">Round Pit</option>
          <option value="Diked Pit">Diked Pit</option>
        </Input>
      </div>
      <span className="usa-error-message" id="new-pit-name-error--message" role="alert">
        <div>{vm.formValidation.formErrors['name']}</div>
      </span>

      {/* No Pit Type Selected */}
      {!vm.newPitForm.type ? <div className="h4 m-4 text-center">Please Enter your Pit Name &amp; Type</div> : null}
      <fieldset disabled={!stores.AppStore.designStore.hasProject() || !stores.AppStore.designStore.hasSurfaceTest()}>
        {/* Round Pit */}
        {vm.newPitForm.type === 'Round Pit' ? (
          <div className="round-pit-diagram-container round-pit">
            <img id="round-structure-diagram" src={roundStructureDiagram} alt="round structure diagram" />
            <label className="usa-label nobr" htmlFor="slope-1">
              Side Slope
            </label>
            <input
              className={
                'usa-input structure-input' + (vm.formValidation['slope-1Valid'] ? '' : vm.errorClass('slope-1'))
              }
              id="slope-1"
              aria-required="true"
              aria-label="Side Slope"
              name="slope-1"
              type="number"
              value={vm.newPitForm.slopes[0]}
              onChange={vm.handleInputChange}
              onBlur={vm.handleOnBlurRounding}
            />
          </div>
        ) : null}

        {/* Rectangular Pit */}
        {vm.newPitForm.type === 'Rectangular Pit' ? (
          <div className="structure-diagram-container">
            <img
              id="rectangular-structure-diagram"
              src={rectangularStructureDiagram}
              alt="rectangular structure diagram"
            />

            <input
              className={'usa-input structure-input' + vm.errorClass('width')}
              id="structure-width"
              aria-label="stucture-width"
              aria-required="true"
              name="width"
              type="number"
              value={vm.newPitForm.width}
              onChange={vm.handleInputChange}
              onBlur={vm.handleOnBlurRounding}
            />
            <input
              className={'usa-input structure-input' + vm.errorClass('length')}
              id="structure-length"
              aria-label="structure-length"
              aria-required="true"
              name="length"
              type="number"
              value={vm.newPitForm.length}
              onChange={vm.handleInputChange}
              onBlur={vm.handleOnBlurRounding}
            />
            {/* Slopes */}
            <label className="usa-label" htmlFor="slope-1" id="slope-1-label">
              ss
            </label>
            <input
              className={
                'usa-input structure-input' + (vm.formValidation['slope-1Valid'] ? '' : vm.errorClass('slope-1'))
              }
              id="slope-1"
              aria-required="true"
              name="slope-1"
              type="number"
              value={vm.newPitForm.slopes[0]}
              onChange={vm.handleInputChange}
              onBlur={vm.handleOnBlurRounding}
            />
            <label className="usa-label" htmlFor="slope-2" id="slope-2-label">
              ss
            </label>
            <input
              className={
                'usa-input structure-input' + (vm.formValidation['slope-2Valid'] ? '' : vm.errorClass('slope-2'))
              }
              id="slope-2"
              aria-required="true"
              name="slope-2"
              type="number"
              value={vm.newPitForm.slopes[1]}
              onChange={vm.handleInputChange}
              onBlur={vm.handleOnBlurRounding}
            />
            <label className="usa-label" htmlFor="slope-3" id="slope-3-label">
              ss
            </label>
            <input
              className={
                'usa-input structure-input' + (vm.formValidation['slope-3Valid'] ? '' : vm.errorClass('slope-3'))
              }
              id="slope-3"
              aria-required="true"
              name="slope-3"
              type="number"
              value={vm.newPitForm.slopes[2]}
              onChange={vm.handleInputChange}
              onBlur={vm.handleOnBlurRounding}
            />
            <label className="usa-label" htmlFor="slope-4" id="slope-4-label">
              ss
            </label>
            <input
              className={
                'usa-input structure-input' + (vm.formValidation['slope-4Valid'] ? '' : vm.errorClass('slope-4'))
              }
              id="slope-4"
              aria-required="true"
              name="slope-4"
              type="number"
              value={vm.newPitForm.slopes[3]}
              onChange={vm.handleInputChange}
              onBlur={vm.handleOnBlurRounding}
            />
          </div>
        ) : null}

        {/* Rectangular Pit Equal Slope and Pick Up Center*/}
        {vm.newPitForm.type === 'Rectangular Pit' ? (
          <>
            <span className="usa-error-message" id="new-pit-error--message" role="alert">
              <div>{vm.formValidation.formErrors['width']}</div>
              <div>{vm.formValidation.formErrors['length']}</div>
              <div>{vm.slopeErrorMsg}</div>
              <div>{vm.errorMsg}</div>
            </span>
            <div className="structure-equal-slopes">
              <input
                className="usa-checkbox__input"
                data-behavior="select-table-row"
                id="equalSlopes"
                type="checkbox"
                name="equalSlopes"
                value="equalSlopes"
                checked={vm.newPitForm.equalSlopes}
                onChange={vm.toggleEqualSlopes}
              />
              <label className="usa-checkbox__label align-items-center" htmlFor="equalSlopes">
                Equal Side Slopes
              </label>
              <div className="pick-center-btn">
                <FlatButton
                  title={'select center from map'}
                  iconLeft={mdiTargetVariant}
                  onClick={() => {
                    // vm.pickUpCenter();
                    pitDesignStore.chooseCenter ? pitDesignStore.chooseCenter = false : pitDesignStore.chooseCenter = true;
                    props.chooseCenterPoint();
                    vm.clearCenterErrorMessages();
                    vm.checkStructurePendingEdits('center');
                  }}
                  className={` ${pitDesignStore.chooseCenter ? 'active' : ''}`}
                  disabled={stores.AppStore.designStore.sceneViewActive}
                >
                  <span>Select Center</span>
                </FlatButton>
                <div className="choose-center-warn-msg">only available on 2D view</div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 offset-md-6 rect-move-rotate">
                <FlatButton
                  title="move and rotate the structure on the map"
                  iconLeft={mdiCursorMove}
                  aria-label={`${vm.sketchActive ? 'active' : ''}`}
                  className={`move-rotate-button ${vm.sketchActive ? 'active' : ''}`}
                  onClick={() => {
                    // console.log('Move Button');
                    vm.activateMoveRotateSketch();
                    vm.checkStructurePendingEdits('center');
                  }}
                >
                  <span>Move/Rotate</span>
                </FlatButton>
              </div>
            </div>
          </>
        ) : null}

        {/* Diked Pit Image and Slope Inputs*/}
        {vm.newPitForm.type === 'Diked Pit' ? (
          <div className="structure-diagram-container">
            <img
              ref={ref}
              onLoad={onLoad}
              id="diked-structure-diagram"
              src={dikedStructureDiagram}
              alt="diked structure diagram"
            />
            {loaded ? (
              <>
                <label className="usa-label" id="pit-bottom-width-label">
                  BL
                </label>
                <label className="usa-label" id="pit-bottom-length-label">
                  BW
                </label>
                {/* Cut Slopes */}
                <label className="usa-label" htmlFor="pit-cut-slope-1" id="pit-cut-slope-1-label">
                  CS1
                </label>
                <input
                  className={
                    'usa-input structure-input' +
                    (vm.formValidation['pit-cut-slope-1Valid'] ? '' : vm.errorClass('pit-cut-slope-1'))
                  }
                  id="pit-cut-slope-1"
                  aria-required="true"
                  name="pit-cut-slope-1"
                  type="number"
                  value={vm.newPitForm.cutSlopes[0]}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <label className="usa-label" htmlFor="pit-cut-slope-2" id="pit-cut-slope-2-label">
                  CS2
                </label>
                <input
                  className={
                    'usa-input structure-input' +
                    (vm.formValidation['pit-cut-slope-2Valid'] ? '' : vm.errorClass('pit-cut-slope-2'))
                  }
                  id="pit-cut-slope-2"
                  aria-required="true"
                  name="pit-cut-slope-2"
                  type="number"
                  value={vm.newPitForm.cutSlopes[1]}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <label className="usa-label" htmlFor="pit-cut-slope-3" id="pit-cut-slope-3-label">
                  CS3
                </label>
                <input
                  className={
                    'usa-input structure-input' +
                    (vm.formValidation['pit-cut-slope-3Valid'] ? '' : vm.errorClass('pit-cut-slope-3'))
                  }
                  id="pit-cut-slope-3"
                  aria-required="true"
                  name="pit-cut-slope-3"
                  type="number"
                  value={vm.newPitForm.cutSlopes[2]}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <label className="usa-label" htmlFor="pit-cut-slope-4" id="pit-cut-slope-4-label">
                  CS4
                </label>
                <input
                  className={
                    'usa-input structure-input' +
                    (vm.formValidation['pit-cut-slope-4Valid'] ? '' : vm.errorClass('pit-cut-slope-4'))
                  }
                  id="pit-cut-slope-4"
                  aria-required="true"
                  name="pit-cut-slope-4"
                  type="number"
                  value={vm.newPitForm.cutSlopes[3]}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                {/* Fill Slopes */}
                <label className="usa-label" htmlFor="pit-fill-slope-1" id="pit-fill-slope-1-label">
                  FS1
                </label>
                <input
                  className={
                    'usa-input structure-input' +
                    (vm.formValidation['pit-fill-slope-1Valid'] ? '' : vm.errorClass('pit-fill-slope-1'))
                  }
                  id="pit-fill-slope-1"
                  aria-required="true"
                  name="pit-fill-slope-1"
                  type="number"
                  value={vm.newPitForm.fillSlopes[0]}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <label className="usa-label" htmlFor="pit-fill-slope-2" id="pit-fill-slope-2-label">
                  FS2
                </label>
                <input
                  className={
                    'usa-input structure-input' +
                    (vm.formValidation['pit-fill-slope-2Valid'] ? '' : vm.errorClass('pit-fill-slope-2'))
                  }
                  id="pit-fill-slope-2"
                  aria-required="true"
                  name="pit-fill-slope-2"
                  type="number"
                  value={vm.newPitForm.fillSlopes[1]}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <label className="usa-label" htmlFor="pit-fill-slope-3" id="pit-fill-slope-3-label">
                  FS3
                </label>
                <input
                  className={
                    'usa-input structure-input' +
                    (vm.formValidation['pit-fill-slope-3Valid'] ? '' : vm.errorClass('pit-fill-slope-3'))
                  }
                  id="pit-fill-slope-3"
                  aria-required="true"
                  name="pit-fill-slope-3"
                  type="number"
                  value={vm.newPitForm.fillSlopes[2]}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <label className="usa-label" htmlFor="pit-fill-slope-4" id="pit-fill-slope-4-label">
                  FS4
                </label>
                <input
                  className={
                    'usa-input structure-input' +
                    (vm.formValidation['pit-fill-slope-4Valid'] ? '' : vm.errorClass('pit-fill-slope-4'))
                  }
                  id="pit-fill-slope-4"
                  aria-required="true"
                  name="pit-fill-slope-4"
                  type="number"
                  value={vm.newPitForm.fillSlopes[3]}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                {/* Side Slopes */}
                <label className="usa-label" htmlFor="pit-side-slope-1" id="pit-side-slope-1-label">
                  SS1
                </label>
                <input
                  className={
                    'usa-input structure-input' +
                    (vm.formValidation['pit-side-slope-1Valid'] ? '' : vm.errorClass('pit-side-slope-1'))
                  }
                  id="pit-side-slope-1"
                  aria-required="true"
                  name="pit-side-slope-1"
                  type="number"
                  value={vm.newPitForm.sideSlopes[0]}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <label className="usa-label" htmlFor="pit-side-slope-2" id="pit-side-slope-2-label">
                  SS2
                </label>
                <input
                  className={
                    'usa-input structure-input' +
                    (vm.formValidation['pit-side-slope-2Valid'] ? '' : vm.errorClass('pit-side-slope-2'))
                  }
                  id="pit-side-slope-2"
                  aria-required="true"
                  name="pit-side-slope-2"
                  type="number"
                  value={vm.newPitForm.sideSlopes[1]}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <label className="usa-label" htmlFor="pit-side-slope-3" id="pit-side-slope-3-label">
                  SS3
                </label>
                <input
                  className={
                    'usa-input structure-input' +
                    (vm.formValidation['pit-side-slope-3Valid'] ? '' : vm.errorClass('pit-side-slope-3'))
                  }
                  id="pit-side-slope-3"
                  aria-required="true"
                  name="pit-side-slope-3"
                  type="number"
                  value={vm.newPitForm.sideSlopes[2]}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <label className="usa-label" htmlFor="pit-side-slope-4" id="pit-side-slope-4-label">
                  SS4
                </label>
                <input
                  className={
                    'usa-input structure-input' +
                    (vm.formValidation['pit-side-slope-4Valid'] ? '' : vm.errorClass('pit-side-slope-4'))
                  }
                  id="pit-side-slope-4"
                  aria-required="true"
                  name="pit-side-slope-4"
                  type="number"
                  value={vm.newPitForm.sideSlopes[3]}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
              </>
            ) : null}
            {/* Bottom Width and Length Labels */}
          </div>
        ) : null}

        {/* Diked Pit Equal Slopes and Pick Up Center */}
        {vm.newPitForm.type === 'Diked Pit' ? (
          <>
            <span className="usa-error-message" id="new-pit-error--message" role="alert">
              <div>{vm.formValidation.formErrors['width']}</div>
              <div>{vm.formValidation.formErrors['length']}</div>
              <div>{vm.formValidation.formErrors['bottomWidth']}</div>
              <div>{vm.formValidation.formErrors['bottomLength']}</div>
              <div>{vm.formValidation.formErrors['bottomElevation']}</div>
              <div>{vm.formValidation.formErrors['topWidth']}</div>
              <div>{vm.formValidation.formErrors['orienAzimuth']}</div>
              <div>{vm.formValidation.formErrors['topElevation']}</div>
              <div>{vm.formValidation.formErrors['radius']}</div>
              <div>{vm.slopeErrorMsg}</div>
              <div>{vm.errorMsg}</div>
            </span>
            <div className="row">
              <div className="col-md-6">
                {/* Side Slope */}
                <input
                  className="usa-checkbox__input"
                  data-behavior="select-table-row"
                  id="equalSideSlopes"
                  type="checkbox"
                  name="equalSideSlopes"
                  value="equalSideSlopes"
                  checked={vm.newPitForm.equalSideSlopes}
                  onChange={vm.toggleEqualSideSlopes}
                />
                <label className="usa-checkbox__label align-items-center" htmlFor="equalSideSlopes">
                  Equal Side Slopes
                </label>
                {/* Fill Slope */}
                <input
                  className="usa-checkbox__input"
                  data-behavior="select-table-row"
                  id="equalFillSlopes"
                  type="checkbox"
                  name="equalFillSlopes"
                  value="equalFillSlopes"
                  checked={vm.newPitForm.equalFillSlopes}
                  onChange={vm.toggleEqualFillSlopes}
                />
                <label className="usa-checkbox__label align-items-center" htmlFor="equalFillSlopes">
                  Equal Fill Slopes
                </label>
              </div>

              <div className="col-md-6">
                {/* Cut Slope */}
                <input
                  className="usa-checkbox__input"
                  data-behavior="select-table-row"
                  id="equalCutSlopes"
                  type="checkbox"
                  name="equalCutSlopes"
                  value="equalCutSlopes"
                  checked={vm.newPitForm.equalCutSlopes}
                  onChange={vm.toggleEqualCutSlopes}
                />
                <label className="usa-checkbox__label align-items-center" htmlFor="equalCutSlopes">
                  Equal Cut Slopes
                </label>

                <div className="pick-center-btn">
                  <FlatButton
                    title={'select center from map'}
                    iconLeft={mdiTargetVariant}
                    onClick={() => {
                      // vm.pickUpCenter();
                      pitDesignStore.chooseCenter ? pitDesignStore.chooseCenter = false : pitDesignStore.chooseCenter = true;
                      props.chooseCenterPoint();
                      vm.clearCenterErrorMessages();
                      vm.checkStructurePendingEdits('center');
                    }}
                    className={` ${pitDesignStore.chooseCenter ? 'active' : ''}`}
                    disabled={stores.AppStore.designStore.sceneViewActive}
                  >
                    <span>Select Center</span>
                  </FlatButton>
                  <div className="choose-center-warn-msg">only available on 2D view</div>
                </div>
                <div className=" mb-2">
                  <FlatButton
                    title="move and rotate the structure on the map"
                    iconLeft={mdiCursorMove}
                    aria-label={`${vm.sketchActive ? 'active' : ''}`}
                    className={`move-rotate-button ${vm.sketchActive ? 'active' : ''}`}
                    onClick={() => {
                      // console.log('Move Button Diked');
                      vm.activateMoveRotateSketch();
                      vm.checkStructurePendingEdits('center');
                    }}
                  // Diked Pit
                  >
                    <span>Move/Rotate</span>
                  </FlatButton>
                </div>
              </div>
            </div>
          </>
        ) : null}

        {/* Round Pit */}
        {vm.newPitForm.type === 'Round Pit' ? (
          <>
            <span className="usa-error-message" id="new-pit-error--message" role="alert">
              <div>{vm.slopeErrorMsg}</div>
              <div>{vm.errorMsg}</div>
            </span>
            <div className="row pick-up-center-div">
              <div className="pick-center-btn">
                <FlatButton
                  title={'select center from map'}
                  iconLeft={mdiTargetVariant}
                  onClick={() => {
                    // vm.pickUpCenter();
                    pitDesignStore.chooseCenter ? pitDesignStore.chooseCenter = false : pitDesignStore.chooseCenter = true;
                    props.chooseCenterPoint();
                    vm.clearCenterErrorMessages();
                    vm.checkStructurePendingEdits('center');
                  }}
                  className={` ${pitDesignStore.chooseCenter ? 'active' : ''}`}
                  disabled={stores.AppStore.designStore.sceneViewActive}
                >
                  <span>Select Center</span>
                </FlatButton>
                <div className="choose-center-warn-msg">only available on 2D view</div>
              </div>
              <div>
                <FlatButton
                  title="move the structure on the map"
                  iconLeft={mdiCursorMove}
                  aria-label={`${vm.sketchActive ? 'active' : ''}`}
                  className={`move-rotate-button ${vm.sketchActive ? 'active' : ''}`}
                  onClick={() => {
                    vm.activateMoveRotateSketch();
                    vm.checkStructurePendingEdits('center');
                  }}
                >
                  <span aria-hidden={true}>Move</span>
                </FlatButton>
              </div>
            </div>
          </>
        ) : null}

        {vm.newPitForm.type === 'Diked Pit' ? (
          <div className="row diked-pit-inputs">
            <div className="col-md-4">
              <label className="usa-label" htmlFor="topWidth">
                Top Width
              </label>
              <div className="input-div">
                <input
                  className={'usa-input' + vm.errorClass('topWidth')}
                  id="topWidth"
                  aria-required="true"
                  aria-describedby="topWidth-unit"
                  name="topWidth"
                  type="number"
                  value={vm.newPitForm.topWidth}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <span className="pit-metric-units" id="topWidth-unit">
                  ft
                </span>
              </div>
              <label className="usa-label" htmlFor="topElevation">
                Top Elevation
              </label>
              <div className="input-div">
                <input
                  className={`usa-input ${vm.errorClass('topElevation')} ${vm.usePitDepth ? 'disabled' : ''}`}
                  disabled={vm.usePitDepth}
                  id="topElevation"
                  aria-required="true"
                  name="topElevation"
                  aria-describedby="topElevation-unit"
                  type="number"
                  value={toDecimalPrecision(vm.newPitForm.topElevation, 3)}
                  onChange={vm.handlePitDepthChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <span className="pit-metric-units" id="topElevation-unit">
                  ft
                </span>
              </div>
              <label className="usa-label" htmlFor="x-center">
                Pit Center X
              </label>
              <div className="input-div">
                <input
                  className="usa-input"
                  id="x-center"
                  aria-required="true"
                  aria-describedby="pitCenterX-unit"
                  name="x"
                  type="number"
                  value={toDecimalPrecision(vm.newPitForm.center.x, 3)}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
              </div>
            </div>

            <div className="col-md-4">
              <label className="usa-label" htmlFor="bottomWidth">
                Bottom Width
              </label>
              <div className="input-div">
                <input
                  className={'usa-input' + vm.errorClass('bottomWidth')}
                  id="bottomWidth"
                  aria-required="true"
                  aria-describedby="bottomWidth-unit"
                  name="bottomWidth"
                  type="number"
                  value={vm.newPitForm.bottomWidth}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <span className="pit-metric-units" id="bottomWidth-unit">
                  ft
                </span>
              </div>
              <label className="usa-label" htmlFor="bottomElevation">
                Bottom Elevation
              </label>
              <div className="input-div">
                <input
                  className={'usa-input' + vm.errorClass('bottomElevation')}
                  id="bottomElevation"
                  aria-required="true"
                  aria-describedby="bottomElevation-unit"
                  name="bottomElevation"
                  type="number"
                  value={toDecimalPrecision(vm.newPitForm.bottomElevation, 3)}
                  onChange={vm.handlePitDepthChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <span className="pit-metric-units" id="bottomElevation-unit">
                  ft
                </span>
              </div>
              <label className="usa-label" htmlFor="y-center">
                Pit Center Y
              </label>
              <div className="input-div">
                <input
                  className="usa-input"
                  id="y-center"
                  aria-required="true"
                  aria-describedby="pitCenterY-unit"
                  name="y"
                  type="number"
                  value={toDecimalPrecision(vm.newPitForm.center.y, 3)}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
              </div>
            </div>

            <div className="col-md-4">
              <label className="usa-label" htmlFor="bottomLength">
                Bottom Length
              </label>
              <div className="input-div">
                <input
                  className={'usa-input' + vm.errorClass('bottomLength')}
                  id="bottomLength"
                  aria-required="true"
                  aria-describedby="bottomLength-unit"
                  name="bottomLength"
                  type="number"
                  value={vm.newPitForm.bottomLength}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <span className="pit-metric-units" id="bottomLength-unit">
                  ft
                </span>
              </div>
              <div className="pit-depth">
                <label className="usa-label mr-5" htmlFor="pitDepth">
                  Pit Depth
                </label>
                <input
                  className="usa-checkbox__input"
                  data-behavior="select-table-row"
                  id="pitDepthCheckBox"
                  aria-label="Use Pit Depth"
                  type="checkbox"
                  name="pitDepthCheckBox"
                  value="pitDepthCheckBox"
                  checked={vm.usePitDepth}
                  onChange={vm.toggleUsePitDepth}
                />

                <label className="usa-checkbox__label pit-depth-checkbox" htmlFor="pitDepthCheckBox"></label>
                <input
                  className={`usa-input ${!vm.usePitDepth ? 'disabled' : ''}`}
                  disabled={!vm.usePitDepth}
                  id="pitDepth"
                  aria-required="true"
                  aria-describedby="pitDepth-unit"
                  name="pitDepth"
                  type="number"
                  value={toDecimalPrecision(vm.newPitForm.pitDepth, 2)}
                  onChange={vm.handlePitDepthChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <span className="pit-metric-units" id="pitDepth-unit">
                  ft
                </span>
              </div>

              <label className="usa-label" htmlFor="orienAzimuth">
                Rotation Azimuth
              </label>
              <div className="input-div">
                <input
                  className={'usa-input' + vm.errorClass('orienAzimuth')}
                  id="orienAzimuth"
                  aria-required="true"
                  name="orienAzimuth"
                  aria-describedby="orienAzimuth-unit"
                  type="number"
                  value={toDecimalPrecision(vm.newPitForm.orienAzimuth, 2)}
                  onChange={vm.handleInputChange}
                  onBlur={vm.handleOnBlurRounding}
                />
                <span className="pit-metric-units" id="orienAzimuth-unit">
                  &#176;
                </span>
              </div>
            </div>
          </div>
        ) : null}

        {vm.newPitForm.type && vm.newPitForm.type !== 'Diked Pit' ? (
          <div className="row">
            <div className="col-md-6">
              <label className="usa-label" htmlFor="x-center">
                Center X
              </label>
              <input
                className={'usa-input' + vm.errorClass('x')}
                id="x-center"
                aria-required="true"
                name="x"
                type="number"
                value={toDecimalPrecision(vm.newPitForm.center.x, 3)}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              <span className="usa-error-message" id="new-pit-error--message-x-y-z" role="alert">
                {vm.formValidation.formErrors['x'] && (
                  <div>Center X {vm.formValidation.formErrors['x']}</div>
                )}
              </span>
              <label className="usa-label" htmlFor="z-center">
                Bottom Z
              </label>
              <input
                className={'usa-input' + vm.errorClass('z')}
                id="z-center"
                aria-required="true"
                name="z"
                type="number"
                value={toDecimalPrecision(vm.newPitForm.center.z, 3)}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              <span className="usa-error-message" id="new-pit-error--message-x-y-z" role="alert">
                {vm.formValidation.formErrors['z'] && (
                  <div>Bottom Z {vm.formValidation.formErrors['z']}</div>
                )}
              </span>
            </div>

            <div className="col-md-6">
              <label className="usa-label" htmlFor="y-center">
                Center Y
              </label>
              <input
                className={'usa-input' + vm.errorClass('y')}
                id="y-center"
                aria-required="true"
                name="y"
                type="number"
                value={toDecimalPrecision(vm.newPitForm.center.y, 3)}
                onChange={vm.handleInputChange}
                onBlur={vm.handleOnBlurRounding}
              />
              <span className="usa-error-message" id="new-pit-error--message-x-y-z" role="alert">
                {vm.formValidation.formErrors['y'] && (
                  <div>Center Y {vm.formValidation.formErrors['y']}</div>
                )}
              </span>
              {/* Rectangular Pit Rotation Azimuth Input*/}
              {vm.newPitForm.type === 'Rectangular Pit' ? (
                <div>
                  <label className="usa-label" htmlFor="orienAzimuth">
                    Rotation Azimuth
                  </label>
                  <input
                    className={'usa-input' + vm.errorClass('orienAzimuth')}
                    id="orienAzimuth"
                    aria-required="true"
                    name="orienAzimuth"
                    type="number"
                    value={toDecimalPrecision(vm.newPitForm.orienAzimuth, 2)}
                    onChange={vm.handleInputChange}
                    onBlur={vm.handleOnBlurRounding}
                  />
                  <span className="usa-error-message" id="new-pit-error--message" role="alert">
                    <div>{vm.formValidation.formErrors['orienAzimuth']}</div>
                  </span>
                </div>
              ) : null}

              {/* Round Pit Radius Input*/}
              {vm.newPitForm.type === 'Round Pit' ? (
                <div>
                  <label className="usa-label" htmlFor="radius">
                    Radius
                  </label>
                  <input
                    className={'usa-input' + vm.errorClass('radius')}
                    id="radius"
                    aria-required="true"
                    name="radius"
                    type="number"
                    value={toDecimalPrecision(vm.newPitForm.radius, 2)}
                    onChange={vm.handleInputChange}
                    onBlur={vm.handleOnBlurRounding}
                  />
                  <span className="usa-error-message" id="new-pit-error--message" role="alert">
                    <div>{vm.formValidation.formErrors['radius']}</div>
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {vm.newPitForm.type ? (
          <>
            <div className="row">
              <button
                className="usa-button design-button"
                onClick={vm.generateStructure}
                disabled={
                  vm.isLoading ||
                  (!pitDesignStore.newDesign && pitDesignStore.pendingEdits.name && !pitDesignStore.pendingEdits.design) ||
                  (pitDesignStore.newDesign && !pitDesignStore.pendingEdits.design)
                }>
                Design
              </button>
            </div>

            <div className="row">
              <div className="calculations-container">
                <h4>Structure Calculations</h4>
                {vm.newPitForm.type === 'Diked Pit' ? (
                  <>
                    <div className="col-md-6">
                      <div className="mb-1">
                        Cut Volume (cy):{' '}
                        {pitDesignStore.newPitResults.cutVolume
                          ? toDecimalPrecision(pitDesignStore.newPitResults.cutVolume, 2)
                          : '--'}
                      </div>
                      <div className="mb-1">
                        Cut/Fill Balance (cy):{' '}
                        {pitDesignStore.newPitResults.cutFillBalance
                          ? toDecimalPrecision(pitDesignStore.newPitResults.cutFillBalance, 2)
                          : '--'}
                      </div>
                      <div className="mb-1">
                        Storage Volume (ac-ft):{' '}
                        {pitDesignStore.newPitResults.storageVolume
                          ? toDecimalPrecision(pitDesignStore.newPitResults.storageVolume, 2)
                          : '--'}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-1">
                        Fill Volume (cy):{' '}
                        {pitDesignStore.newPitResults.fillVolume
                          ? toDecimalPrecision(pitDesignStore.newPitResults.fillVolume, 2)
                          : '--'}
                      </div>
                      <div className="mb-1">
                        Cut/Fill Ratio:{' '}
                        {pitDesignStore.newPitResults.cutFillRatio
                          ? toDecimalPrecision(pitDesignStore.newPitResults.cutFillRatio, 2)
                          : '--'}
                      </div>
                      <div className="mb-1">
                        Storage Depth (ft):{' '}
                        {pitDesignStore.newPitResults.storageDepth
                          ? toDecimalPrecision(pitDesignStore.newPitResults.storageDepth, 2)
                          : '--'}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-md-6">
                      <div className="mb-1">
                        Storage Volume (ac-ft):{' '}
                        {pitDesignStore.newPitResults.storageVolume
                          ? toDecimalPrecision(pitDesignStore.newPitResults.storageVolume, 2)
                          : '--'}
                      </div>
                      <div className="mb-1">
                        Storage Depth (ft):{' '}
                        {pitDesignStore.newPitResults.storageDepth
                          ? toDecimalPrecision(pitDesignStore.newPitResults.storageDepth, 2)
                          : '--'}
                      </div>
                    </div>
                    <div className="col-md-6">
                      Cut Volume (cy):{' '}
                      {pitDesignStore.newPitResults.cutVolume
                        ? toDecimalPrecision(pitDesignStore.newPitResults.cutVolume, 2)
                        : '--'}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        ) : null}
      </fieldset>
      <div className="row justify-content-end">
        <div>
          <button
            hidden={!pitDesignStore.editDesign}
            className="usa-button float-right"
            onClick={vm.updateDesignElement}
            disabled={
              //disable the form when form has no change or the numberic form fields are updated after design
              !pitDesignStore.newDesignForm.name ||
              (!pitDesignStore.newDesign && !pitDesignStore.pendingEdits.name && !pitDesignStore.pendingEdits.design) || pitDesignStore.pendingEdits.design
            }
          >
            Accept Edits
          </button>
          <button
            hidden={pitDesignStore.editDesign}
            className="usa-button float-right"
            onClick={vm.postAddDesignElement}
            disabled={
              !pitDesignStore.newDesignForm.name ||
              !pitDesignStore.newPitResults.cutVolume ||
              (!pitDesignStore.newDesign && !pitDesignStore.pendingEdits.name && !pitDesignStore.pendingEdits.design) ||
              pitDesignStore.pendingEdits.design
            }
          >
            Save Structure
          </button>
          <button
            className="usa-button usa-button--outline float-right"
            onClick={() => {
              props.redefineSketch()
              vm.cancelAddDesignElement(true)
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const NewPit = observer(NewPitComponent);
